import React, { useRef, useState } from 'react';
import './TutorialCard.css';

interface TutorialCardProps {
  onAgree: () => void;
}

const SWIPE_THRESHOLD = 120;

const TutorialCard: React.FC<TutorialCardProps> = ({ onAgree }) => {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const mouseDownRef = useRef(false);
  const [swipeDX, setSwipeDX] = useState(0);
  const [swipeDY, setSwipeDY] = useState(0);
  const [leaving, setLeaving] = useState<'left' | 'right' | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [nudgeRight, setNudgeRight] = useState(false);

  const beginDrag = (clientX: number, clientY: number) => {
    touchStartX.current = clientX;
    touchStartY.current = clientY;
    setSwipeDX(0);
    setSwipeDY(0);
    setLeaving(null);
  };

  const updateDrag = (clientX: number, clientY: number) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    setSwipeDX(clientX - touchStartX.current);
    setSwipeDY(clientY - touchStartY.current);
  };

  const resetPosition = () => {
    setSwipeDX(0);
    setSwipeDY(0);
    setLeaving(null);
  };

  const endDrag = () => {
    if (touchStartX.current == null || touchStartY.current == null) {
      resetPosition();
      return;
    }
    const dx = swipeDX;
    const dy = swipeDY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (absX > absY && absX > SWIPE_THRESHOLD) {
      if (dx > 0) {
        // Agree: swipe right to proceed
        setLeaving('right');
        setTimeout(() => {
          onAgree();
        }, 220);
      } else {
        // Disagree: swipe left should swipe the card away then reset
        setLeaving('left');
        setShowHint(true);
        setTimeout(() => {
          resetPosition();
          setNudgeRight(true);
          setTimeout(() => {
            setNudgeRight(false);
          }, 600);
        }, 220);
      }
    } else {
      resetPosition();
    }
  };

  const onTouchStart = (event: React.TouchEvent) => {
    const touch = event.changedTouches[0];
    beginDrag(touch.clientX, touch.clientY);
  };

  const onTouchMove = (event: React.TouchEvent) => {
    const touch = event.changedTouches[0];
    updateDrag(touch.clientX, touch.clientY);
  };

  const onTouchEnd = () => {
    endDrag();
  };

  const onMouseDown = (event: React.MouseEvent) => {
    mouseDownRef.current = true;
    beginDrag(event.clientX, event.clientY);
  };

  const onMouseMove = (event: React.MouseEvent) => {
    if (!mouseDownRef.current) return;
    updateDrag(event.clientX, event.clientY);
  };

  const onMouseUp = () => {
    if (!mouseDownRef.current) return;
    mouseDownRef.current = false;
    endDrag();
  };

  const onMouseLeave = () => {
    if (!mouseDownRef.current) return;
    mouseDownRef.current = false;
    resetPosition();
  };

  const style: React.CSSProperties = {
    transform: `translate(${swipeDX * 0.4}px, ${swipeDY * 0.2}px) rotate(${swipeDX * 0.04}deg)`,
  };

  const showLeftBadge = swipeDX < -40 || leaving === 'left';
  const showRightBadge = swipeDX > 40 || leaving === 'right';

  return (
    <div className="tutorial-card-stage">
      <div
        className={`tutorial-card ${leaving === 'left' ? 'leaving-left' : ''} ${
          leaving === 'right' ? 'leaving-right' : ''
        } ${nudgeRight ? 'tutorial-card-nudge-right' : ''}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        style={style}
      >
        <div className="tutorial-card-content">
          <div className="tutorial-row">
            <div className="tutorial-icon" aria-hidden="true">
              <img
                src="/mbti/Swipe-Left-Finger.svg"
                alt="Swipe left"
                className="tutorial-icon"
              />
            </div>
            <p className="tutorial-text">Swipe left to disagree</p>
          </div>
          <div className="tutorial-row">
            <div className="tutorial-icon" aria-hidden="true">
              <img
                src="/mbti/Swipe-Right-Finger.svg"
                alt="Swipe right"
                className="tutorial-icon"
              />
            </div>
            <p className="tutorial-text">Swipe right to agree</p>
          </div>
        </div>
        {showLeftBadge && <div className="tutorial-badge left">&#10005;</div>}
        {showRightBadge && <div className="tutorial-badge right">&#10003;</div>}
        {showHint && (
          <div className="tutorial-hint-overlay">
            <div className="tutorial-hint-bubble">Swipe right to continue</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorialCard;
